"use client";

import { forwardRef, useState, ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from "react";
import { Magnetic } from "./Magnetic";
import { useMotionMode } from "@/context/MotionContext";

type ButtonVariant = "primary" | "secondary" | "icon" | "icon-sm";

interface ButtonBaseProps {
  variant?: ButtonVariant;
  children: ReactNode;
  className?: string;
  magnetic?: boolean;
  magneticStrength?: number;
}

type ButtonAsButton = ButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> & {
    as?: "button";
    href?: never;
  };

type ButtonAsAnchor = ButtonBaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps> & {
    as: "a";
    href: string;
  };

type ButtonProps = ButtonAsButton | ButtonAsAnchor;

const variantStyles: Record<ButtonVariant, { base: string; hoverFill?: boolean; isIcon?: boolean }> = {
  primary: { base: "px-[var(--btn-padding-x)] py-[var(--btn-padding-y)] rounded-full text-[length:var(--btn-font-size)] font-medium", hoverFill: true },
  secondary: { base: "px-[var(--btn-padding-x-sm)] py-[var(--btn-padding-y-sm)] rounded-full text-[length:var(--btn-font-size-sm)]" },
  icon: { base: "w-[var(--btn-size-icon)] h-[var(--btn-size-icon)] rounded-full", isIcon: true },
  "icon-sm": { base: "w-[var(--btn-size-icon-sm)] h-[var(--btn-size-icon-sm)] rounded-full", isIcon: true },
};

const baseStyles = "inline-flex items-center justify-center gap-2 cursor-pointer transition-all duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)] focus-visible:ring-[var(--theme-base)]";

const glassStyles = {
  isolation: "isolate" as const,
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
};

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = "secondary",
      children,
      className = "",
      magnetic = true,
      magneticStrength, // Uses Magnetic default (--magnetic-strength token)
      ...rest
    } = props;

    const { mode: motionMode } = useMotionMode();

    // Check if button is disabled (from rest props)
    const isDisabled = rest.as !== "a" && (rest as ButtonHTMLAttributes<HTMLButtonElement>).disabled;

    const [isHovered, setIsHovered] = useState(false);
    const variantConfig = variantStyles[variant];
    const hasHoverFill = variantConfig.hoverFill;
    const isIcon = variantConfig.isIcon;
    const combinedClassName = `${baseStyles} glass ${variantConfig.base} ${isDisabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`;

    // Scale uses CSS variable tokens for configurability
    const scaleVar = isIcon ? "var(--btn-scale-icon)" : "var(--btn-scale)";

    // Hover styles - scale for all, fill effect for CTA (disabled when button is disabled)
    const canHover = isHovered && !isDisabled;
    const hoverStyles: React.CSSProperties = {
      transform: canHover ? `scale(${scaleVar})` : "scale(1)",
      transition: motionMode === "off" ? "none" : "transform var(--duration-normal) var(--ease-out), color var(--duration-normal) var(--ease-out), background-color var(--duration-normal) var(--ease-out), border-color var(--duration-normal) var(--ease-out), box-shadow var(--duration-normal) var(--ease-out)",
      ...(hasHoverFill && !isDisabled ? {
        color: canHover ? "var(--background)" : "var(--theme-fg)",
        backgroundColor: canHover ? "var(--theme-fg)" : undefined,
        borderColor: canHover ? "var(--theme-fg)" : undefined,
        boxShadow: canHover
          ? "0 0 var(--btn-glow-sm) rgba(var(--theme-base-rgb), var(--btn-glow-opacity)), 0 0 var(--btn-glow-lg) rgba(var(--theme-base-rgb), var(--btn-glow-opacity-soft))"
          : "none",
      } : {}),
    };

    const combinedStyles = { ...glassStyles, ...hoverStyles };

    const hoverHandlers = {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      onFocus: () => setIsHovered(true),
      onBlur: () => setIsHovered(false),
    };

    const content =
      rest.as === "a" ? (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={combinedClassName}
          style={combinedStyles}
          {...hoverHandlers}
          {...(rest as Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "as">)}
        >
          {children}
        </a>
      ) : (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          className={combinedClassName}
          style={combinedStyles}
          {...hoverHandlers}
          {...(rest as Omit<ButtonHTMLAttributes<HTMLButtonElement>, "as">)}
        >
          {children}
        </button>
      );

    if (magnetic && !isDisabled) {
      return (
        <Magnetic strength={magneticStrength}>
          {content}
        </Magnetic>
      );
    }

    return content;
  }
);

// Text CTA - minimal text link with icon
import { Tooltip } from "./Tooltip";

interface TextCTAProps {
  children: ReactNode;
  icon?: ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
  tooltip?: string;
  tooltipPosition?: "top" | "bottom" | "left" | "right" | "top-left" | "top-right" | "bottom-left" | "bottom-right";
}

export function TextCTA({ children, icon, href, onClick, className = "", tooltip, tooltipPosition = "bottom" }: TextCTAProps) {
  const { mode: motionMode } = useMotionMode();

  const baseClass = `text-sm font-mono font-semibold tracking-wide inline-flex items-center gap-2 gradient-text rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)] focus-visible:ring-[var(--theme-base)] ${motionMode === "on" ? "transition-transform duration-200 hover:scale-110" : ""} ${className}`;

  const content = href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={baseClass}
      onClick={onClick}
    >
      {children}
      {icon}
    </a>
  ) : (
    <button onClick={onClick} className={`cursor-pointer ${baseClass}`}>
      {children}
      {icon}
    </button>
  );

  if (tooltip) {
    return (
      <Tooltip text={tooltip} position={tooltipPosition}>
        {content}
      </Tooltip>
    );
  }

  return content;
}

// Icon components for common actions
// All icons use consistent stroke width from CSS token
const ICON_STROKE = "var(--btn-icon-stroke)";

// Icon size constants - match CSS tokens in globals.css
export const ICON_SIZE_SM = 14;
export const ICON_SIZE_MD = 18;
export const ICON_SIZE_LG = 24;

export const CloseIcon = ({ size = ICON_SIZE_LG }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const BackIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <path d="M19 12H5m0 0l6 6m-6-6l6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ArrowRightIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <path d="M5 12h14m0 0l-6 6m6-6l-6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronLeftIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <path d="M16 20l-8-8 8-8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronRightIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <path d="M8 20l8-8-8-8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronUpIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <path d="M4 16l8-8 8 8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const SunIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <circle cx="12" cy="12" r="5" fill="currentColor" stroke="none" />
    <line x1="12" y1="1" x2="12" y2="3" strokeLinecap="round" />
    <line x1="12" y1="21" x2="12" y2="23" strokeLinecap="round" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" strokeLinecap="round" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" strokeLinecap="round" />
    <line x1="1" y1="12" x2="3" y2="12" strokeLinecap="round" />
    <line x1="21" y1="12" x2="23" y2="12" strokeLinecap="round" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" strokeLinecap="round" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" strokeLinecap="round" />
  </svg>
);

export const MoonIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
  >
    <path d="M22 13.29A10 10 0 1 1 10.71 2 8 8 0 0 0 22 13.29z" />
  </svg>
);

export const LockIcon = ({ size = ICON_SIZE_MD, className }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
    className={className}
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 11V7a5 5 0 0110 0v4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const UnlockIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 11V7a5 5 0 019.9-1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const MotionOnIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
  >
    {/* Filled play triangle with rounded corners - sized to match sun/moon */}
    <path d="M4 3a1.5 1.5 0 0 1 2.25-1.3l15 9a1.5 1.5 0 0 1 0 2.6l-15 9A1.5 1.5 0 0 1 4 21V3z" />
  </svg>
);

export const PlayIcon = ({ size = 64, className }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
    className={className}
  >
    {/* Filled play triangle with rounded corners - sized to match sun/moon */}
    <path d="M4 3a1.5 1.5 0 0 1 2.25-1.3l15 9a1.5 1.5 0 0 1 0 2.6l-15 9A1.5 1.5 0 0 1 4 21V3z" />
  </svg>
);

export const MotionOffIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
  >
    {/* Filled stop square */}
    <rect x="5" y="5" width="14" height="14" rx="2" />
  </svg>
);

export const ResetIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    {/* Circular arrow for reset */}
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 3v5h5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ExternalLinkIcon = ({ size = ICON_SIZE_MD, muted = false }: { size?: number; muted?: boolean }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={muted ? "rgba(var(--foreground-rgb),0.5)" : "currentColor"}
    style={{ strokeWidth: ICON_STROKE }}
  >
    {/* Arrow pointing up-right with box corner */}
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 3h6v6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 14L21 3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const CopyIcon = ({ size = ICON_SIZE_SM }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="rgba(var(--foreground-rgb),0.5)"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Audio icons
export const SpeakerOnIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const SpeakerOffIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="23" y1="9" x2="17" y2="15" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="17" y1="9" x2="23" y2="15" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// CRT/scanlines icon
export const CRTIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="8" y1="21" x2="16" y2="21" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="17" x2="12" y2="21" strokeLinecap="round" strokeLinejoin="round" />
    {/* Scanlines */}
    <line x1="5" y1="7" x2="19" y2="7" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
    <line x1="5" y1="10" x2="19" y2="10" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
    <line x1="5" y1="13" x2="19" y2="13" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
  </svg>
);

// CRT off icon - monitor without scanlines, with strike-through
export const CRTOffIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="8" y1="21" x2="16" y2="21" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="17" x2="12" y2="21" strokeLinecap="round" strokeLinejoin="round" />
    {/* Strike-through */}
    <line x1="4" y1="1" x2="20" y2="19" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const GearIcon = ({ size = ICON_SIZE_MD }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    style={{ strokeWidth: ICON_STROKE }}
  >
    <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
